import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { noiseFilterAPI } from "./api";
import { logout } from "../auth/";
import { createMiddleware } from "../middlewareUtil";

// Initial state
const initialState = {
  noiseFilterStatus: "",
  selectedFile: {},
  isFiltering: false,
  isFiltered: false,
  guid: "",
  clipId: 0,
  commitId: 0,
  guidLoading: false,
  guidLoaded: true,
  noiseStart: 0.0,
  noiseEnd: 2.0,
  fftLength: 512,
  noiseCancelRatio: 0.75,
  fdMaskAveraging: 500.0,
  tdMaskAveraging: 50.0,
  nStdThreshold: 1.5,

  clipTime: 0,
  originalTime: 0,
  waveOpacity: 0.25,
  regionSelected: false,
  region: {
    start: 0,
    end: 5,
    id: "filter",
  },
};

export const queueNoiseFilter = createAsyncThunk(
  "noiseFilter/queueNoiseFilter",
  async (
    { caseId, clipId, commitId },
    { getState, dispatch, rejectWithValue },
  ) => {
    const accessToken = getState().auth.accessCode;

    const noiseFilterRequest = {
      clipId: clipId,
      commitId: commitId,
      noiseStart: getState().noiseFilter.noiseStart,
      noiseEnd: getState().noiseFilter.noiseEnd,
      fftLength: getState().noiseFilter.fftLength,
      noiseCancelRatio: getState().noiseFilter.noiseCancelRatio,
      fdMaskAveraging: getState().noiseFilter.fdMaskAveraging,
      tdMaskAveraging: getState().noiseFilter.tdMaskAveraging,
      nStdThreshold: getState().noiseFilter.nStdThreshold,
    };

    const result = await noiseFilterAPI.queueNoiseFilter(
      accessToken,
      caseId,
      clipId,
      noiseFilterRequest,
    );

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to queue noise filter request");
  },
);

// Create the slice
const noiseFilterSlice = createSlice({
  name: "noiseFilter",
  initialState,
  reducers: {
    reset: (_state, _action) => {
      return cloneDeep(initialState);
    },
    updateNoiseFilter: (state, action) => {
      Object.assign(state, action.payload);
    },
    setTimeNoiseFilter: (state, action) => {
      const { clipTime, originalTime } = action.payload;
      state.clipTime = clipTime;
      state.originalTime = originalTime;
    },
  },
  extraReducers: (builder) => {
    builder
      // Queue noise filter request
      .addCase(queueNoiseFilter.pending, (state, _action) => {
        state.noiseFilterStatus = "QUEUED";
        state.isFiltering = true;
        state.isFiltered = false;
      })
      .addCase(queueNoiseFilter.fulfilled, (state, _action) => {
        state.noiseFilterStatus = "";
        state.isFiltering = false;
        state.isFiltered = true;
      })
      .addCase(queueNoiseFilter.rejected, (state, _action) => {
        state.noiseFilterStatus = "";
        state.isFiltering = false;
        state.isFiltered = true;
      });
  },
});

export const noiseFilterMiddleware = createMiddleware([
  "noiseFilter/queueNoiseFilter/pending",
]);

// Export actions
export const { reset, updateNoiseFilter, setTimeNoiseFilter } =
  noiseFilterSlice.actions;

export default noiseFilterSlice.reducer;
